import { z } from 'zod';

import { inviteUserFieldsSchema } from '@/pages/Business/pages/Users/models';
import { emptyMobilePhone } from '@/utils/phoneNumber';

/**
 * Wizard de integración de comercios: comercio -> sucursales -> invitaciones.
 *
 * Un comercio real tiene varias sucursales y varias personas usándolo (el dueño
 * con acceso a todo, un encargado por sucursal), así que los pasos 2 y 3
 * acumulan listas en vez de un único elemento.
 *
 * No hay endpoint atómico que cree todo junto, así que cada alta confirma
 * contra la API antes de sumarse a la lista. Eso es lo que permite reintentar
 * sin duplicar: lo que ya está en la lista, ya existe en el backend.
 */

export enum IntegrationStepId {
  COMMERCE = 'commerce',
  BRANCH = 'branch',
  INVITE = 'invite',
  API_REQUEST = 'api-request',
  DONE = 'done',
}

export const INTEGRATION_STEPS: { id: IntegrationStepId; label: string }[] = [
  { id: IntegrationStepId.COMMERCE, label: 'Comercio' },
  { id: IntegrationStepId.BRANCH, label: 'Sucursales' },
  { id: IntegrationStepId.INVITE, label: 'Usuarios' },
  { id: IntegrationStepId.API_REQUEST, label: 'Integración API (opcional)' },
  { id: IntegrationStepId.DONE, label: 'Resumen' },
];

export const stepIndexOf = (stepId: IntegrationStepId): number =>
  INTEGRATION_STEPS.findIndex((step) => step.id === stepId);

/** Cómo se resolvió un elemento: eligiendo algo existente o creándolo acá. */
export type StepSourceMode = 'existing' | 'new';

export interface IntegrationCommerce {
  id: string;
  name: string;
  businessName: string;
  externalId?: string;
  /** true si el comercio lo creó este wizard (no si ya existía). */
  created: boolean;
}

export interface IntegrationBranch {
  id: string;
  name: string;
  created: boolean;
}

/** Alcance con el que se invita a una persona. */
export type InviteScopeMode = 'commerce' | 'branches';

export interface IntegrationInvite {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  /** Teléfono tal como se mandó al backend. */
  phoneNumber: string;
  invitationLink: string;
  /** false cuando el usuario quedó creado pero el mail de invitación falló. */
  emailSent: boolean;
  scopeMode: InviteScopeMode;
  /** Sucursales asignadas cuando el alcance no es todo el comercio. */
  branchIds: string[];
  /** Texto ya resuelto del alcance, para el resumen. */
  scopeSummary: string;
}

export interface IntegrationWizardState {
  stepIndex: number;
  commerce: IntegrationCommerce | null;
  branches: IntegrationBranch[];
  /** Se confirmó seguir sin sucursales: el alcance queda a nivel comercio. */
  branchesConfirmed: boolean;
  invites: IntegrationInvite[];
  invitesConfirmed: boolean;
  apiRequestId: string | null;
  apiRequestSkipped: boolean;
}

export const emptyIntegrationWizardState: IntegrationWizardState = {
  stepIndex: 0,
  commerce: null,
  branches: [],
  branchesConfirmed: false,
  invites: [],
  invitesConfirmed: false,
  apiRequestId: null,
  apiRequestSkipped: false,
};

/**
 * El progreso vive en sessionStorage: si se recarga la pestaña en medio de la
 * integración no se pierde lo que ya se creó en el backend.
 *
 * v2 porque la forma cambió de un elemento por paso a listas; un estado v1
 * guardado no se puede migrar a ciegas.
 */
export const INTEGRATION_WIZARD_STORAGE_KEY =
  'business:commerce-integration-wizard:v3';

/**
 * Datos de una persona a invitar. Reusa las reglas por campo de la invitación
 * normal; el alcance sale de las sucursales cargadas en el paso anterior.
 */
export const integrationInviteSchema = inviteUserFieldsSchema
  .pick({
    firstName: true,
    lastName: true,
    email: true,
    phone: true,
  })
  .extend({
    roleId: z.string().min(1, 'Debes seleccionar un rol'),
    scopeMode: z.enum(['commerce', 'branches']),
    branchIds: z.array(z.string()),
  })
  .superRefine((data, ctx) => {
    if (data.scopeMode === 'branches' && data.branchIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['branchIds'],
        message: 'Elegí al menos una sucursal para esta persona',
      });
    }
  });

export type IntegrationInviteSchema = z.infer<typeof integrationInviteSchema>;

export const defaultIntegrationInviteValues: IntegrationInviteSchema = {
  firstName: '',
  lastName: '',
  email: '',
  phone: emptyMobilePhone(),
  roleId: '',
  scopeMode: 'commerce',
  branchIds: [],
};

/** Texto del alcance para mostrar en las listas y el resumen. */
export const buildScopeSummary = (
  scopeMode: InviteScopeMode,
  branchIds: string[],
  branches: IntegrationBranch[]
): string => {
  if (scopeMode === 'commerce') {
    return 'Todo el comercio';
  }

  const names = branchIds
    .map((id) => branches.find((branch) => branch.id === id)?.name)
    .filter((name): name is string => Boolean(name));

  return names.length > 0 ? names.join(', ') : 'Sucursales seleccionadas';
};
